import {Inject, Injectable, OnDestroy} from "@angular/core";
import {BehaviorSubject, Subscription} from "rxjs";
import {NavigationService} from "../navigation/navigation.service";
import {Order} from "../domain/order";
import {Address} from "../domain/address";
import {Cart} from "../domain/cart";
import {OrderLine} from "../domain/orderline";
import {CartItem} from "../domain/cartitem";
import {ExchangePriceControllerService, OrderControllerService} from "@aparraga/ahoracrypto-api-sdk/sdk";
import {CartService} from "../cart/cart.service";
import {AccountService} from "../account/account.service";
import {CryptoService} from "../crypto/crypto.service";
import {IS_BROWSER} from "../utils/injectiontokens";
import {StringUtils} from "../utils/stringutils";
import {OrderTotals} from "../domain/ordertotals";
import {LocalStorageService} from "../storage/local-storage.service";

@Injectable({
  providedIn: 'root'
})
export class OrderService  implements OnDestroy {

  public order: Order = null;
  public order$: BehaviorSubject<Order>;
  protected couponDiscountCalculationSubject: BehaviorSubject<Order>;

  protected subscriptions: Subscription[] = new Array<Subscription>();

  public constructor(protected navigationService: NavigationService,
                     protected orderControllerService: OrderControllerService,
                     protected cartService: CartService,
                     protected stringUtils: StringUtils,
                     @Inject(IS_BROWSER) protected isBrowser: boolean,
                     protected cryptoService: CryptoService,
                     protected exchangePriceController: ExchangePriceControllerService,
                     protected accountService: AccountService,
                     protected storageService: LocalStorageService) {

    if(this.isBrowser) {
      this.order = this.storageService.getObject<Order>("Order");
    }

    if(this.order == null) {
      this.order = new Order();
      if(navigationService.getGclid() != null) {
        this.order.gclid = navigationService.getGclid();
      }
      if(navigationService.getReferral() != null) {
        this.order.referral = navigationService.getReferral();
      }
      navigationService.getAnalyticsClientId().then((clientId: string) => {
        this.order.analyticsClientId = clientId;
      });
    }

    this.order$ = new BehaviorSubject(this.order);

    this.subscriptions.push(this.accountService.userSubject$.subscribe(user => {
      if(user != null) {
        let billingAddress = new Address();
        if(user.address != null) {
          billingAddress = user.address;
        }
        billingAddress.email = user.email;
        billingAddress.phone = user.phone;

        this.order.billingAddress = billingAddress;

        //save the order just in case it hasn't been
        if(this.order.id == null) {
          this.saveOrder(this.order);
        }

      }
    }));

    //intercept each change in the shopping cart => update current order => notify subscribers
    this.subscriptions.push(this.cartService.shoppingCart$.subscribe(cart => {
      if(!this.areLinesEqual(cart, this.order)) {
        this.order.orderLines = this.createOrderLinesFromCartItems(cart);
        if(cart != null && !!cart.cartItems && cart.cartItems.length > 0) {
          this.order.currencyIsoCode = this.cryptoService.getPeggedCurrency(cart.cartItems[0].exchangePrice.quoteCurrency);
        }
        this.order$.next(this.order);
        if(this.order.couponCode != null) {
          this.couponDiscountCalculationSubject.next(this.order);
        }
        //now update order on server side
        if(!this.accountService.isAnonymous()) {
          this.saveOrder(this.order);
        }
      }
    }));

  }

  public getOrderWithFeeAndDiscounts() {

    return new Promise<OrderTotals>((resolve, reject) => {

      let orderTotals = new OrderTotals();

      this.calculatePriceAdjustments(this.order).then((order: any) => {

        let items = order.orderLines;
        if (items.length == 1) { //need to review this line!!!

          let orderLine = items[0];
          orderTotals.cryptoAmountCanVary = orderLine.cryptoQuantityCanVary;
          orderTotals.baseCurrencyPrecision = orderLine.product.precision;
          let priceAdjustments = orderLine.priceAdjustmentsDTO;
          orderTotals.baseAmountAfterFees = priceAdjustments.baseAmountAfterFees;
          orderTotals.quoteAmountAfterFees = this.stringUtils.formatCurrency(priceAdjustments.quoteAmountAfterFees);
          orderTotals.totalCommissionAmount = 0;
          orderTotals.totalDiscountAmount = 0;
          priceAdjustments.priceAdjustments.forEach(priceAdjustment => {
            if (priceAdjustment.type == 1) {
              if (priceAdjustment.concept == 1) {
                if (orderLine.cryptoQuantityCanVary) {
                  orderTotals.networkFee = priceAdjustment.baseFeeAmount;
                } else {
                  orderTotals.networkFee = priceAdjustment.quoteFeeAmount;
                }
                orderTotals.totalCommissionAmount += orderTotals.networkFee;
              } else if (priceAdjustment.concept == 2) {
                orderTotals.commissionAmountPercentage = priceAdjustment.quoteFee;
                if (orderLine.cryptoQuantityCanVary) {
                  orderTotals.commissionAmount = priceAdjustment.baseFeeAmount;
                } else {
                  orderTotals.commissionAmount = priceAdjustment.quoteFeeAmount;
                }
                orderTotals.totalCommissionAmount += orderTotals.commissionAmount;
              }
            }
            else if (priceAdjustment.type == 2) {
              orderTotals.totalDiscountAmount += priceAdjustment.baseFeeAmount;
              orderTotals.discountConcept = priceAdjustment.name;
            }
          });

          orderTotals.currencyAmount = this.stringUtils.formatCurrency(items[0].totalToPayFor);
          orderTotals.cryptoAmount = items[0].quantity;

          resolve(orderTotals);
        }
        else {
          reject(false);
        }

      });

    });

  }

  public clearOrder() {
    this.order = new Order();
    this.storageService.removeItem('Order');
    this.order$.next(this.order);
  }

  public getAddressEmail(address: Address) {
    let returnValue: string = null;
    if(address != null) {
      returnValue = address.email;
    }
    return returnValue;
  }

  public areLinesEqual(cart: Cart, order: Order) {
    let returnValue = true; //by default, lines as equals
    if(cart == null || cart.cartItems == null || cart.cartItems.length != order.orderLines.length) {
      returnValue = false;
    }
    else {
      for (let i = 0; i < cart.cartItems.length; i++) {
        if (cart.cartItems[i].product.id != order.orderLines[i].product.id ||
          cart.cartItems[i].exchangePrice.reservationToken != order.orderLines[i].exchangePrice.reservationToken ||
          cart.cartItems[i].walletAddress != order.orderLines[i].walletAddress ||
          cart.cartItems[i].walletNetwork != order.orderLines[i].walletNetwork ||
          cart.cartItems[i].walletMemo != order.orderLines[i].walletMemo ||
          cart.cartItems[i].quantity != order.orderLines[i].quantity) {
          returnValue = false;
        }
      }
    }
    return returnValue;
  }

  public ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public createOrderLinesFromCartItems(cart: Cart) {

    let returnValue: Array<OrderLine> = new Array();

    if(cart != null && cart.cartItems != null) {
      let items: CartItem[] = cart.cartItems;
      items.forEach((item: CartItem) => {

        let orderLine = new OrderLine();
        orderLine.product = item.product;
        orderLine.exchangePrice = item.exchangePrice;
        orderLine.quantity = item.quantity;
        orderLine.cryptoQuantityCanVary = item.cryptoQuantityCanVary;
        orderLine.walletAddress = item.walletAddress;
        orderLine.walletNetwork = item.walletNetwork;
        orderLine.walletMemo = item.walletMemo;
        if (item.totalToPayFor != null && item.totalToPayFor > 0) {
          orderLine.totalToPayFor = item.totalToPayFor;
        } else {
          orderLine.totalToPayFor = item.exchangePrice.price * item.quantity;
        }
        orderLine.totalToPayFor = item.totalToPayFor;
        returnValue.push(orderLine);

      });
    }

    return returnValue;

  }

  public getOrder() {
    return this.order;
  }

  public setOrder(order: Order) {
    this.order = order;
    this.order$.next(this.order);
  }

  public calculatePriceAdjustments(order: Order) {
    let promise = new Promise((resolve, reject) => {
      this.orderControllerService.orderControllerCalculatePriceAdjustments(order).subscribe(
        (order) => {
          resolve(order);
        },
        (error) => {
          reject(error);
        }
      );
    });

    return promise;

  }

  public getOrderLineFees(orderLine: OrderLine) {

    let promise = new Promise((resolve, reject) => {

      let amount = orderLine.quantity;
      let type = 1;
      if(orderLine.cryptoQuantityCanVary) {
        amount = orderLine.totalToPayFor;
        type = 2;
      }

      /*
      this.exchangePriceController.exchangePriceControllerCalculateExchangeFees(orderLine.exchangePrice.reservationToken, amount, type, orderLine.walletNetwork).subscribe(
        (exchangeFees) => {
          resolve(exchangeFees);
        },
        (error) => {
          reject(error);
        }
      );
       */

    });

    return promise;

  }

  public getOrderTotal() {
    //return the sum of all orderline totalToPayFor values
    let returnValue = 0;
    this.order.orderLines.forEach((orderLine: OrderLine) => {
      returnValue += orderLine.totalToPayFor;
    });
    return returnValue;
  }

  public saveOrder(order: Order) {

    if(this.isBrowser) {
      //never save an order with no orderlines and without id
      if(order.id == null && (order.orderLines == null || order.orderLines.length == 0)) {
        return;
      }
      this.orderControllerService.orderControllerSaveOrder(order).subscribe(result => {
        if (result != null && result.hasOwnProperty("id")) {
          this.order.id = result['id'];
          this.order.orderNumber = result['orderNumber'];
        }
        this.storageService.setObject('Order', this.order);
      });
    }

  }

}
